import { Component, OnInit, Input } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import * as Rx from 'rxjs'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { MagliveApiClient } from 'src/lib/api-client/maglive-api-client'
import { FirestoreAccess } from 'src/lib/firestore-access'
import { environment } from 'src/env/environment'
import { concatMap, tap, first } from 'rxjs/operators'
import { ToastrService } from 'ngx-toastr'
import {
  Gift,
  GiftPutRequest,
  GiftIconPutResponse,
  GiftAnimationPutResponse,
} from 'src/lib/entity/gift'
@Component({
  selector: 'maglive-admin-gift-edit',
  templateUrl: './gift-edit.component.html',
  styleUrls: [
    '../../../../pages/admin/startbootstrap-sb-admin-gh-pages/src/scss/styles.scss',
    './gift-edit.component.scss',
  ],
})
export class AdminGiftEditComponent implements OnInit {
  private baseURl =
    environment.name === 'local'
      ? 'https://live.stg.mag2.com/'
      : environment.baseURL.magliveWeb
  public defaultGiftIconUrl: string
  private uploadedIconEndpoint: string // 登録中のギフトアイコンのフルパス

  // フォーム関係
  formGroup: FormGroup
  selectedGiftIconFile: File
  selectedGiftAnimationFile: File
  registeredGiftAnimationFileName: String // 登録中のギフトアニメーションのファイル名
  isWaiting: boolean

  private giftId: string
  @Input() gift: Gift

  constructor(
    private fb: FormBuilder,
    private fsa: FirestoreAccess,
    private toastr: ToastrService,
    private route: ActivatedRoute,
    private router: Router
  ) {
    this.route.params.subscribe((params) => (this.giftId = params['id']))
  }

  private removeFirstSlash = (target: string): string =>
    target.replace(/^\//, '')
  private generateFullUrl = (path: string): string =>
    this.baseURl + this.removeFirstSlash(path)
  private extractFileName = (path: string): string =>
    path.substring(path.lastIndexOf('/') + 1)

  ngOnInit(): void {
    if (!this.giftId) this.router.navigate(['admin/gifts'])
    this.fsa.getGift(this.giftId).subscribe((x) => {
      this.gift = x
      this.defaultGiftIconUrl =
        environment.baseURL.magliveWeb + 'assets/images/noimage.png'
      this.uploadedIconEndpoint = this.generateFullUrl(x.iconPath)

      const initIconUrl = x.iconPath
        ? this.uploadedIconEndpoint
        : this.defaultGiftIconUrl
      if (x.animationPath) {
        this.registeredGiftAnimationFileName = this.extractFileName(
          x.animationPath
        )
      }

      const numPattern = /^[0-9]+$/
      this.formGroup = this.fb.group({
        name: [x.name, [Validators.required]],
        point: [
          x.point,
          [
            Validators.required,
            Validators.min(1),
            Validators.pattern(numPattern),
            Validators.max(100000000),
          ],
        ],
        displayOrder: [
          x.displayOrder,
          [
            Validators.required,
            Validators.min(0),
            Validators.pattern(numPattern),
            Validators.max(100000000),
          ],
        ],
        publicStatus: [
          x.publicStatus,
          [Validators.required, Validators.pattern('PUBLIC|PRIVATE')],
        ],
        iconPath: [x.iconPath],
        iconUrl: [initIconUrl, [Validators.required]],
        iconFile: [
          null,
          this.uploadedIconEndpoint === initIconUrl
            ? []
            : [Validators.required],
        ],
        animationPath: [x.animationPath],
        animationFile: [
          null,
          this.selectedGiftAnimationFile ? [Validators.required] : [],
        ],
      })
    })
  }

  get name() {
    return this.formGroup.get('name')
  }
  get point() {
    return this.formGroup.get('point')
  }
  get displayOrder() {
    return this.formGroup.get('displayOrder')
  }
  get publicStatus() {
    return this.formGroup.get('publicStatus')
  }
  get iconPath() {
    return this.formGroup.get('iconPath')
  }
  get iconUrl() {
    return this.formGroup.get('iconUrl')
  }
  get iconFile() {
    return this.formGroup.get('iconFile')
  }
  get animationPath() {
    return this.formGroup.get('animationPath')
  }
  get animationFile() {
    return this.formGroup.get('animationFile')
  }

  /**
   * @param {string} path ギフトファイルのパスex) /data/gifts/icons/〇〇
   * @returns {string} ギフトファイルのフルパス
   */
  getGiftFilePath = (path: string) => {
    if (environment.name === 'local') {
      return `https://live.stg.mag2.com${path}`
    } else {
      // pathの先頭スラッシュは不要なので除去する
      return environment.baseURL.magliveWeb + path.replace(/\//, '')
    }
  }

  clickGiftIconButton(event: any): void {
    event.target.value = ''
  }

  clickGiftAnimationButton(event: any): void {
    event.target.value = ''
  }

  changeGiftIcon(event: any): void {
    const reader = new FileReader()
    if (event.target.files.length > 0) {
      const file = event.target.files[0]
      this.selectedGiftIconFile = file
      reader.onload = (_) => {
        let image = new Image()
        image.src = reader.result as string
        this.formGroup.patchValue({
          iconUrl: reader.result,
        })
      }
      reader.readAsDataURL(file)
    }
  }

  removeSelectingGiftIcon(): void {
    if (this.iconUrl.value != this.defaultGiftIconUrl) {
      this.selectedGiftIconFile = null
      this.formGroup.patchValue({
        iconPath: null,
        iconUrl: this.generateFullUrl(this.gift.iconPath),
      })
    }
  }

  changeGiftAnimation(event: any): void {
    if (event.target.files.length > 0) {
      const file = event.target.files[0]
      this.selectedGiftAnimationFile = file
    }
  }

  removeSelectingGiftAnimation(): void {
    this.selectedGiftAnimationFile = null
    this.formGroup.patchValue({
      animationPath: null,
    })
  }

  private updateGiftIcon(
    iconFile: File
  ): Rx.Observable<'NOT_AUTHORIZED' | 'BAD_REQUEST' | GiftIconPutResponse> {
    const formData = new FormData()
    formData.append('file', iconFile)

    return MagliveApiClient.updateGiftIcon(this.gift.id, formData).pipe(
      tap((response) => {
        const result = response as GiftIconPutResponse
        this.formGroup.patchValue({
          iconPath: result.giftIconPath,
        })
      })
    )
  }

  private updateGiftAnimation(
    animationFile: File
  ): Rx.Observable<
    'NOT_AUTHORIZED' | 'BAD_REQUEST' | GiftAnimationPutResponse
  > {
    const formData = new FormData()
    formData.append('file', animationFile)

    return MagliveApiClient.updateGiftAnimation(this.gift.id, formData).pipe(
      tap((response) => {
        const result = response as GiftAnimationPutResponse
        this.formGroup.patchValue({
          animationPath: result.giftAnimationPath,
        })
      })
    )
  }

  private validateUpdateGift(request: GiftPutRequest): boolean {
    if (request.name == null) return false
    if (request.point == null || request.point < 1) return false
    if (request.displayOrder == null || request.displayOrder < 0) return false
    if (
      request.publicStatus == null ||
      (request.publicStatus !== 'PUBLIC' && request.publicStatus !== 'PRIVATE')
    )
      return false
    if (request.iconPath == null) return false
    if (request.animationPath == null) return false
    return true
  }

  public updateGift() {
    if (this.isWaiting) return
    this.isWaiting = true

    console.log(`this.gift.iconPath: ${this.gift.iconPath}`)
    console.log(`this.iconUrl.value: ${this.iconUrl.value}`)
    console.log(`this.uploadedIconEndpoint: ${this.uploadedIconEndpoint}`)
    console.log(
      `this.uploadedIconEndpoint === this.iconUrl.value: ${
        this.uploadedIconEndpoint === this.iconUrl.value
      }`
    )

    // アイコン登録
    const giftIconPutResponse$ =
      this.uploadedIconEndpoint === this.iconUrl.value
        ? Rx.of(void 0)
        : this.updateGiftIcon(this.selectedGiftIconFile)
    // アニメーション登録
    const giftAnimationPutResponse$ = this.selectedGiftAnimationFile
      ? this.updateGiftAnimation(this.selectedGiftAnimationFile)
      : Rx.of(void 0)

    Rx.forkJoin([
      giftIconPutResponse$.pipe(first()),
      giftAnimationPutResponse$.pipe(first()),
    ])
      .pipe(
        concatMap((data) => {
          const giftIcon = data[0]
          const giftAnimation = data[1]

          if (
            giftIcon === 'NOT_AUTHORIZED' ||
            giftAnimation === 'NOT_AUTHORIZED'
          ) {
            this.toastr.error('権限エラーが発生しました')
            this.isWaiting = false
            return
          }

          if (giftIcon === 'BAD_REQUEST' || giftAnimation === 'BAD_REQUEST') {
            this.toastr.error('入力項目が正しくありません')
            this.isWaiting = false
            return
          }

          const giftPutRequest: GiftPutRequest = {
            name: this.name.value,
            point: this.point.value,
            displayOrder: this.displayOrder.value,
            publicStatus: this.publicStatus.value,
            iconPath: giftIcon ? giftIcon.giftIconPath : this.gift.iconPath,
            animationPath: giftAnimation
              ? giftAnimation.giftAnimationPath
              : this.gift.animationPath,
          }

          if (!this.validateUpdateGift(giftPutRequest)) {
            this.toastr.error('入力項目が正しくありません')
            this.isWaiting = false
            return
          }

          return MagliveApiClient.updateGift(this.gift.id, giftPutRequest)
        })
      )
      .subscribe(
        (response) => {
          if (response === 'NOT_AUTHORIZED') {
            this.toastr.error('権限エラーが発生しました')
            this.isWaiting = false
          } else if (response === 'BAD_REQUEST') {
            this.toastr.error('入力項目が正しくありません')
            this.isWaiting = false
          } else {
            this.toastr.success('更新が完了しました')
            setTimeout(() => (this.isWaiting = false), 3000)
            this.router.navigate(['admin/gifts'])
          }
        },
        (err) => {
          console.error(err)
          this.toastr.error('更新に失敗しました')
          setTimeout(() => (this.isWaiting = false), 3000)
        }
      )
  }
}
